import type { IVntanaAsset, IVntanaProductAssetModel } from '@metmuseum/types';
import {
	VNTANA_ORGANIZATION_SLUG,
	VNTANA_PUBLIC_API_BASE_URL,
} from 'lib/constants.mjs';

const PREFERRED_DOWNLOAD_FORMATS = ['GLB', 'USDZ'] as const;

export interface VntanaDownloadOption {
	extension: string;
	format: string;
	sizeBytes: number;
	url: string;
}

const getFormatRank = (format: string) => {
	const preferredIndex = PREFERRED_DOWNLOAD_FORMATS.indexOf(
		format as (typeof PREFERRED_DOWNLOAD_FORMATS)[number],
	);

	return preferredIndex === -1
		? PREFERRED_DOWNLOAD_FORMATS.length
		: preferredIndex;
};

const isDownloadableModel = (model: IVntanaProductAssetModel) =>
	Boolean(model.modelBlobId) &&
	model.conversionStatus === 'COMPLETED' &&
	PREFERRED_DOWNLOAD_FORMATS.includes(
		model.conversionFormat as (typeof PREFERRED_DOWNLOAD_FORMATS)[number],
	);

const getLicenseAttribute = (
	attributes: IVntanaAsset['attributes'],
): string | null => {
	if (!attributes) return null;
	if (Array.isArray(attributes)) {
		return attributes.find((a) => a.key === 'License')?.value ?? null;
	}
	return attributes['License'] ?? null;
};

/**
 * Returns download options for a Vntana asset.
 *
 * Eligibility rules (in order):
 *   - License === 'CC-NC'  → never downloadable
 *   - License === 'CC0'    → always downloadable (overrides OpenAccess)
 *   - No License attribute → downloadable only when isOpenAccess is true
 */
export const getVntanaDownloadOptions = (
	asset?: IVntanaAsset | null,
	isOpenAccess: boolean = false,
): VntanaDownloadOption[] => {
	if (!asset?.uuid || !asset.clientSlug || !asset.asset?.models?.length) {
		return [];
	}

	const license = getLicenseAttribute(asset.attributes);

	if (license === 'CC-NC') {
		return [];
	}

	if (license !== 'CC0' && !isOpenAccess) {
		return [];
	}

	return asset.asset.models
		.filter(isDownloadableModel)
		.sort(
			(firstModel, secondModel) =>
				getFormatRank(firstModel.conversionFormat) -
				getFormatRank(secondModel.conversionFormat),
		)
		.map((model) => ({
			extension:
				'.' +
				(model.modelBlobId.split('.').pop()?.toLowerCase() ??
					model.conversionFormat.toLowerCase()),
			format: model.conversionFormat,
			sizeBytes: model.modelSize,
			// download=true makes VNTANA return a Content-Disposition header with
			// the original filename, so the browser saves a human-readable name
			// instead of the modelBlobId GUID. See DIGAPPDEV-4498.
			url: `${VNTANA_PUBLIC_API_BASE_URL}/assets/products/${asset.uuid}/organizations/${VNTANA_ORGANIZATION_SLUG}/clients/${asset.clientSlug}/${model.modelBlobId}?download=true`,
		}));
};

export const getPreferredVntanaDownload = (
	asset?: IVntanaAsset | null,
	isOpenAccess: boolean = false,
) => getVntanaDownloadOptions(asset, isOpenAccess)[0] ?? null;
